import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 2.5 visual preview at /preview/lesson/2.5
 *
 * "Week 2 capstone: A full AI workflow, end to end" — Week 2, lesson 5.
 *
 * Capstone shape is different from teaching lessons. One real task, run
 * through the full Week 2 loop. The student is the active operator. Every
 * stage from 2.1 through 2.4 shows up as a step they execute on the same
 * concrete task: pitch a new club to your school's principal.
 *
 * Sections:
 *   1. The brief (the task)
 *   2. The Week 2 loop (visual map)
 *   3. Stage 1: Think first (60-second move from 2.4)
 *   4. Stage 2: Pick the tool (2.1)
 *   5. Stage 3: Write the prompt (2.2)
 *   6. Stage 4: Audit the answer (2.3)
 *   7. Stage 5: Posture and ship (2.4)
 *   8. Lock card: the whole loop
 */

export default function LessonPreviewPage2_5() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

function LessonPage() {
  const totalSections = 8;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const ids = [
      'sec-brief',
      'sec-loop',
      'sec-stage-1',
      'sec-stage-2',
      'sec-stage-3',
      'sec-stage-4',
      'sec-stage-5',
      'sec-lock-card',
    ];
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = ids.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <Brief />
          <LoopMap />
          <Stage1Think />
          <Stage2Tool />
          <Stage3Prompt />
          <Stage4Audit />
          <Stage5Posture />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------- Header ----------

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 2 · Working with AI · Capstone · Lesson 2.5
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        A full AI workflow, end to end
      </h1>
      <p className="mt-4 text-base leading-relaxed text-muted">
        One real task. Five stages. No teaching slides. You run the loop.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 60 min
        </span>
        <span className="h-3 w-px bg-line" />
        <span>Threads: Vocabulary · Judgment · Character · Pattern</span>
        <span className="h-3 w-px bg-line" />
        <span className="text-amber">Closes Week 2</span>
      </div>
    </header>
  );
}

// ---------- 1. The brief ----------

function Brief() {
  return (
    <Section
      id="sec-brief"
      label="The brief · Your real task"
      eyebrow="Read it once. Then we run the loop."
      accent="amber"
    >
      <div className="my-2 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
        <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
          The task
        </p>
        <p className="mt-3 text-lg font-semibold leading-relaxed text-text">
          Write a 3-paragraph email to your school's principal pitching a new
          club you want to start.
        </p>
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <BriefTile
            label="Audience"
            body="Your principal. Busy. Skeptical by default. Reads 40 emails before lunch."
          />
          <BriefTile
            label="Constraints"
            body="Three paragraphs. Under 250 words. Specific ask at the end. No fluff."
          />
          <BriefTile
            label="What makes it land"
            body="A real problem, a clear plan, and a reason the principal should say yes today."
          />
        </div>
      </div>

      <p className="mt-4">
        This is the task you would actually do in real life. Sports captain
        pitch, debate team revival, a coding club, an esports league. The
        topic does not matter for this lesson. The workflow does. We are
        running 2.1, 2.2, 2.3, and 2.4 on one email.
      </p>
    </Section>
  );
}

// ---------- 2. The loop map ----------

function LoopMap() {
  return (
    <Section
      id="sec-loop"
      label="The loop · What you will do"
      eyebrow="Five stages. In order. Every time you use AI for something that matters."
    >
      <div className="my-4 space-y-2">
        {LOOP_STAGES.map((s, i) => (
          <LoopStageCard key={s.label} n={i + 1} {...s} />
        ))}
      </div>
      <p className="mt-2 text-sm text-muted">
        Every stage points back to a Week 2 lesson. If a stage feels fuzzy,
        the lesson is one click away. Today you run all five.
      </p>
    </Section>
  );
}

// ---------- 3. Stage 1: Think first ----------

function Stage1Think() {
  const [draft, setDraft] = useState('');
  const [locked, setLocked] = useState(false);
  return (
    <Section
      id="sec-stage-1"
      label="Stage 1 · Think first"
      eyebrow="60 seconds. No AI yet. From Lesson 2.4."
      accent="amber"
    >
      <p>
        Before you open AI, write your own rough pitch. Two or three messy
        sentences. What is the club, why does it matter, what do you want
        from the principal. Bad sentences are fine. Half-formed ideas are
        fine. The point is that you started with a position.
      </p>

      <div className="my-5 overflow-hidden rounded-xl border border-line bg-surface">
        <div className="flex items-center justify-between border-b border-line bg-elevated/40 px-5 py-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            Your 60-second pitch
          </p>
          <span className="font-mono text-[10px] uppercase tracking-widest text-amber">
            {locked ? 'Locked' : 'Editing'}
          </span>
        </div>
        <div className="px-5 py-5">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={locked}
            placeholder="What is the club? Why does it matter? What do you want from the principal?"
            className="h-32 w-full resize-none rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text placeholder:text-muted/60 focus:border-amber focus:outline-none disabled:opacity-70"
          />
          {!locked && (
            <button
              onClick={() => draft.trim().length > 10 && setLocked(true)}
              className="mt-3 rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover disabled:opacity-50"
              disabled={draft.trim().length <= 10}
            >
              Lock my pitch → Stage 2
            </button>
          )}
          {locked && (
            <div className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/[0.04] p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
                Locked. This is your reference.
              </p>
              <p className="mt-1.5 text-sm text-text/90">
                You will compare AI's answer against this draft at the end.
                The gap is where you learn something or where you decide AI
                is wrong.
              </p>
            </div>
          )}
        </div>
      </div>

      <p className="text-sm text-muted">
        If you skipped this stage and went straight to AI, you would not know
        what you actually thought. You would know what AI thought. Different
        thing.
      </p>
    </Section>
  );
}

// ---------- 4. Stage 2: Pick the tool ----------

function Stage2Tool() {
  const [picked, setPicked] = useState<'fast' | 'smart' | 'specialized' | null>(
    null
  );
  const correct = 'smart';
  return (
    <Section
      id="sec-stage-2"
      label="Stage 2 · Pick the tool"
      eyebrow="From Lesson 2.1. Match the tool to the task."
      accent="blue"
    >
      <p>
        Three tools on the table. One real task: a 250-word email that has to
        land with a busy adult. Which tool fits?
      </p>

      <div className="my-5 grid gap-3 sm:grid-cols-3">
        <ToolPickCard
          id="fast"
          label="Fast and cheap"
          sub="Mini model"
          best="Drills, summaries, quick rewrites, brainstorms."
          picked={picked}
          correct={correct}
          onClick={() => setPicked('fast')}
        />
        <ToolPickCard
          id="smart"
          label="Smart and careful"
          sub="Sonnet-class model"
          best="Writing that goes out under your name. Reasoning. Anything that needs to land."
          picked={picked}
          correct={correct}
          onClick={() => setPicked('smart')}
        />
        <ToolPickCard
          id="specialized"
          label="Specialized"
          sub="Image, code, search"
          best="When the task is not text. Not this task."
          picked={picked}
          correct={correct}
          onClick={() => setPicked('specialized')}
        />
      </div>

      {picked && (
        <div
          className={`my-4 rounded-md border px-4 py-3 ${
            picked === correct
              ? 'border-emerald-500/30 bg-emerald-500/[0.04]'
              : 'border-amber/40 bg-amber/[0.04]'
          }`}
        >
          <p
            className={`font-mono text-[10px] uppercase tracking-widest ${
              picked === correct ? 'text-emerald-300' : 'text-amber'
            }`}
          >
            {picked === correct ? 'Correct' : 'Not quite'} · Model verdict
          </p>
          <p className="mt-1.5 text-sm text-text/90">
            Smart and careful. This email goes out under your name to a
            skeptical adult who decides whether the club exists. Mini would
            give you a generic three-paragraph blob. Specialized is the wrong
            shape entirely. The cost of using a better model on a 250-word
            task is nothing. The cost of a weak email to your principal is
            the club.
          </p>
        </div>
      )}
    </Section>
  );
}

// ---------- 5. Stage 3: Write the prompt ----------

function Stage3Prompt() {
  const [picked, setPicked] = useState<'weak' | 'strong' | null>(null);
  return (
    <Section
      id="sec-stage-3"
      label="Stage 3 · Write the prompt"
      eyebrow="From Lesson 2.2. The five-part prompt does the work."
      accent="amber"
    >
      <p>
        Two prompts for the same task. Pick the one you would actually send.
        Then we will see what comes back.
      </p>

      <div className="my-5 grid gap-3 lg:grid-cols-2">
        <PromptCard
          id="weak"
          label="Prompt A"
          tag="Weak"
          body={'"Help me write an email to my principal about starting a new club."'}
          picked={picked}
          onClick={() => setPicked('weak')}
        />
        <PromptCard
          id="strong"
          label="Prompt B"
          tag="Strong"
          body={`"You are a high school senior writing a 3-paragraph email to the principal of your school. You want to start a [esports club / debate team / etc.]. Audience: a skeptical, busy principal. Constraints: 3 paragraphs, under 250 words, specific ask at the end. Cover: (1) the real problem this solves at our school, (2) the concrete plan including a faculty advisor I have already lined up, (3) a specific ask: a 15-minute meeting next week. Tone: respectful, direct, not flattering. Output: the email only, no commentary."`}
          picked={picked}
          onClick={() => setPicked('strong')}
        />
      </div>

      {picked === 'weak' && (
        <div className="my-4 rounded-md border border-amber/40 bg-amber/[0.04] px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
            Not quite · Model verdict
          </p>
          <p className="mt-1.5 text-sm text-text/90">
            Prompt A skips Role, Context, Constraints, and Output shape. The
            AI will guess at all four. You will get a generic email that
            sounds like every other student. Then you will spend more time
            fixing it than you would have spent writing the strong prompt in
            the first place.
          </p>
        </div>
      )}
      {picked === 'strong' && (
        <div className="my-4 rounded-md border border-emerald-500/30 bg-emerald-500/[0.04] px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
            Correct · Model verdict
          </p>
          <p className="mt-1.5 text-sm text-text/90">
            Prompt B hits all five parts: Role, Task, Context, Constraints,
            Output shape. The AI has no room to drift. You will get
            something usable on the first pass.
          </p>
        </div>
      )}

      {picked === 'strong' && (
        <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
          <div className="border-b border-line bg-elevated/40 px-5 py-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
              AI response · Sonnet-class model · 218 words
            </p>
          </div>
          <div className="space-y-3 px-5 py-5 text-sm leading-relaxed text-text/90">
            <p>
              <span className="text-muted">Subject:</span> Proposing an
              esports club at Lincoln High
            </p>
            <p>
              Dr. Patel, I am writing to propose a new esports club at
              Lincoln High. Right now we have 30 to 40 students who play
              competitively at home but have no school-sanctioned outlet, no
              coach, and no path to compete against other schools. Three
              nearby districts already have varsity esports teams, and last
              year ours sent two students to a regional tournament on their
              own.
            </p>
            <p>
              The plan is straightforward: weekly Thursday meetings in
              Room 214 from 3:30 to 5:00, starting with five core games
              chosen for clean competitive structure. Ms. Reyes in the CS
              department has agreed to serve as faculty advisor. Funding for
              the first semester would come from member dues and a small
              donation we have already secured from a local game store. I
              have a one-page budget and code of conduct ready to share.
            </p>
            <p>
              Could we meet for 15 minutes next week to walk through the
              proposal? I can come during my free period on Tuesday or
              Wednesday. Thank you for the time.
            </p>
            <p className="text-muted">— Maya Chen, 11th grade</p>
          </div>
        </div>
      )}
    </Section>
  );
}

// ---------- 6. Stage 4: Audit the answer ----------

function Stage4Audit() {
  const [zone, setZone] = useState<'trust' | 'verify' | 'walk-away' | null>(
    null
  );
  const correct = 'verify';
  return (
    <Section
      id="sec-stage-4"
      label="Stage 4 · Audit the answer"
      eyebrow="From Lesson 2.3. Pick the zone. Then verify."
      accent="rose"
    >
      <p>
        You have an AI draft. Before you do anything with it, run the trust
        ladder. This email goes out under your name to an adult who can say
        no. What zone is it in?
      </p>

      <div className="my-5 grid gap-3 sm:grid-cols-3">
        <ZonePick
          id="trust"
          label="Trust"
          sub="Use it. Move on."
          zone={zone}
          correct={correct}
          onClick={() => setZone('trust')}
        />
        <ZonePick
          id="verify"
          label="Verify"
          sub="Check the bones."
          zone={zone}
          correct={correct}
          onClick={() => setZone('verify')}
        />
        <ZonePick
          id="walk-away"
          label="Walk away"
          sub="Wrong tool entirely."
          zone={zone}
          correct={correct}
          onClick={() => setZone('walk-away')}
        />
      </div>

      {zone && zone !== correct && (
        <div className="my-4 rounded-md border border-amber/40 bg-amber/[0.04] px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
            Not quite · Model verdict
          </p>
          <p className="mt-1.5 text-sm text-text/90">
            Verify. Anything going out under your name to a skeptical reader
            is verify-zone by default. Trust is for brainstorms. Walk-away
            is for things AI cannot answer. This is squarely verify.
          </p>
        </div>
      )}

      {zone === correct && (
        <>
          <div className="my-4 rounded-md border border-emerald-500/30 bg-emerald-500/[0.04] px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
              Correct · Model verdict
            </p>
            <p className="mt-1.5 text-sm text-text/90">
              Verify. The email mentions specific facts: "Ms. Reyes agreed,"
              "three nearby districts have teams," "donation from a local
              game store," "regional tournament." Each of those is a claim
              you will defend if the principal asks. The AI generated them
              from your prompt. Some are details you gave it. Some it made
              up.
            </p>
          </div>

          <AuditPanel />
        </>
      )}
    </Section>
  );
}

// ---------- 7. Stage 5: Posture and ship ----------

function Stage5Posture() {
  const [mode, setMode] = useState<
    'sparring' | 'drafter' | 'tutor' | 'editor' | null
  >(null);
  const correct = 'editor';
  const [final, setFinal] = useState('');
  const [shipped, setShipped] = useState(false);
  return (
    <Section
      id="sec-stage-5"
      label="Stage 5 · Posture and ship"
      eyebrow="From Lesson 2.4. Pick the mode. Then write it yourself."
      accent="amber"
    >
      <p>
        You have a 60-second draft (yours), an AI draft (the response above),
        and an audit (claims to verify). What teammate mode finishes the
        job?
      </p>

      <div className="my-5 grid gap-3 sm:grid-cols-2">
        {(
          [
            { id: 'sparring', label: 'Sparring partner', sub: 'Push back on my take.' },
            { id: 'drafter', label: 'Drafter', sub: 'Give me a starting point.' },
            { id: 'tutor', label: 'Tutor', sub: 'Explain until I get it.' },
            { id: 'editor', label: 'Editor', sub: 'Find what is broken.' },
          ] as const
        ).map((m) => (
          <ModeButton
            key={m.id}
            label={m.label}
            sub={m.sub}
            selected={mode === m.id}
            verdict={
              mode
                ? correct === m.id
                  ? 'right'
                  : mode === m.id
                  ? 'wrong'
                  : null
                : null
            }
            onClick={() => setMode(m.id)}
          />
        ))}
      </div>

      {mode && mode !== correct && (
        <div className="my-4 rounded-md border border-amber/40 bg-amber/[0.04] px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
            Not quite · Model verdict
          </p>
          <p className="mt-1.5 text-sm text-text/90">
            Editor. You already have a draft (yours, from Stage 1) and the AI
            draft (from Stage 3). Sparring is for before you have a position.
            Drafter is for the blank page. Tutor is for learning a concept.
            Editor is what you need: find what is broken, rewrite it
            yourself.
          </p>
        </div>
      )}

      {mode === correct && (
        <>
          <div className="my-4 rounded-md border border-emerald-500/30 bg-emerald-500/[0.04] px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
              Correct · Model verdict
            </p>
            <p className="mt-1.5 text-sm text-text/90">
              Editor. You point. AI cuts. You decide what stays. The final
              email is yours, in your voice, with claims you have verified.
            </p>
          </div>

          <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
            <div className="flex items-center justify-between border-b border-line bg-elevated/40 px-5 py-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
                Your final email · Written by you · Edited with AI
              </p>
              <span className="font-mono text-[10px] uppercase tracking-widest text-amber">
                {shipped ? 'Shipped' : 'Drafting'}
              </span>
            </div>
            <div className="px-5 py-5">
              <textarea
                value={final}
                onChange={(e) => setFinal(e.target.value)}
                disabled={shipped}
                placeholder="Open the AI draft. Cut what is generic. Keep what is true. Rewrite the rest in your voice. Three paragraphs. Under 250 words. Specific ask at the end."
                className="h-44 w-full resize-none rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text placeholder:text-muted/60 focus:border-amber focus:outline-none disabled:opacity-70"
              />
              {!shipped && (
                <button
                  onClick={() => final.trim().length > 30 && setShipped(true)}
                  className="mt-3 rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover disabled:opacity-50"
                  disabled={final.trim().length <= 30}
                >
                  Ship it →
                </button>
              )}
              {shipped && (
                <div className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/[0.04] p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
                    Shipped · Week 2 complete
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-text/90">
                    You just ran the full loop. Your pitch, your tool pick,
                    your prompt, your audit, your edit, your voice. The AI
                    was in the room. It did not write the email.
                  </p>
                </div>
              )}
            </div>
          </div>

          {shipped && <CompareView />}
        </>
      )}
    </Section>
  );
}

// ---------- 8. Lock card ----------

function LockCard() {
  return (
    <Section
      id="sec-lock-card"
      label="Lock card · The Week 2 loop"
      eyebrow="Screenshot this. Run it any time AI is in the room for something that matters."
    >
      <div className="rounded-xl border border-amber/40 bg-gradient-to-br from-amber/[0.05] to-amber/[0.02] p-8" data-lockcard-body>
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
            Week 2 · Lock card · The full loop
          </p>
          <button className="text-xs text-muted hover:text-text">
            ⤓ Save image
          </button>
        </div>

        <p className="mt-4 text-lg leading-relaxed text-text">
          Five stages. In order. Every time AI matters.
        </p>

        <ol className="mt-6 space-y-3">
          {LOOP_STAGES.map((s, i) => (
            <li
              key={s.label}
              className="flex items-start gap-4 rounded-lg border border-line bg-ink/60 p-4"
            >
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-amber/50 bg-amber/10 font-mono text-xs font-semibold text-amber">
                {String(i + 1).padStart(2, '0')}
              </span>
              <div className="flex-1">
                <div className="flex items-baseline gap-3">
                  <p className="font-semibold text-text">{s.label}</p>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                    Lesson {s.lesson}
                  </span>
                </div>
                <p className="mt-1 text-sm leading-relaxed text-text/90">
                  {s.lockLine}
                </p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-6 rounded-lg border border-line bg-ink/60 p-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            The Week 2 principle
          </p>
          <p className="mt-2 text-base leading-relaxed text-text">
            AI is a teammate that does work with you and for you. It is not a
            replacement for human connection and communication. The loop
            keeps your hands on the parts that matter: the thinking, the
            voice, the decision. AI handles the friction. You handle the
            work.
          </p>
        </div>

        <p className="mt-6 text-sm italic text-muted">
          Think. Pick. Prompt. Audit. Posture. Ship. Every time.
        </p>
      </div>
    </Section>
  );
}

// ---------- Components ----------

const LOOP_STAGES: {
  label: string;
  lesson: string;
  short: string;
  lockLine: string;
}[] = [
  {
    label: 'Think first',
    lesson: '2.4',
    short: '60 seconds. Your draft. No AI yet.',
    lockLine:
      'Spend 60 seconds writing what you already think. Bad sentences welcome. You need a position to compare against.',
  },
  {
    label: 'Pick the tool',
    lesson: '2.1',
    short: 'Match the model to the stakes.',
    lockLine:
      'Match the tool to the task. Mini for drills. Smart and careful for anything going out under your name.',
  },
  {
    label: 'Write the prompt',
    lesson: '2.2',
    short: 'Five parts. Specific in, specific out.',
    lockLine:
      'Role, Task, Context, Constraints, Output shape. Five parts. Vague in, vague out. Specific in, specific out.',
  },
  {
    label: 'Audit the answer',
    lesson: '2.3',
    short: 'Pick the zone. Verify if it matters.',
    lockLine:
      'Trust, verify, or walk away. If it goes out under your name, verify. If it is a question only you can answer, walk away.',
  },
  {
    label: 'Posture and ship',
    lesson: '2.4',
    short: 'Editor mode. You write the final.',
    lockLine:
      'Pick the mode: spar, draft, tutor, edit. Then write the final yourself. AI was in the room. You did the work.',
  },
];

function LoopStageCard({
  n,
  label,
  lesson,
  short,
}: {
  n: number;
  label: string;
  lesson: string;
  short: string;
}) {
  return (
    <div className="flex items-center gap-4 rounded-lg border border-line bg-surface p-4">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber font-mono text-xs font-semibold text-ink">
        {String(n).padStart(2, '0')}
      </span>
      <div className="min-w-0 flex-1 sm:flex sm:items-baseline sm:gap-4">
        <p className="font-semibold text-text sm:w-40 sm:shrink-0">{label}</p>
        <p className="text-sm leading-relaxed text-text/90">{short}</p>
      </div>
      <span className="hidden shrink-0 font-mono text-[10px] uppercase tracking-widest text-muted sm:inline">
        Lesson {lesson}
      </span>
    </div>
  );
}

function BriefTile({ label, body }: { label: string; body: string }) {
  return (
    <div className="rounded-md border border-line bg-ink/40 px-4 py-3">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        {label}
      </p>
      <p className="mt-1.5 text-sm leading-relaxed text-text/90">{body}</p>
    </div>
  );
}

function ToolPickCard({
  id,
  label,
  sub,
  best,
  picked,
  correct,
  onClick,
}: {
  id: 'fast' | 'smart' | 'specialized';
  label: string;
  sub: string;
  best: string;
  picked: 'fast' | 'smart' | 'specialized' | null;
  correct: 'fast' | 'smart' | 'specialized';
  onClick: () => void;
}) {
  const selected = picked === id;
  const showResult = picked !== null;
  const isRight = id === correct;
  const borderClass = showResult
    ? isRight && selected
      ? 'border-emerald-500/50 bg-emerald-500/[0.05]'
      : isRight
      ? 'border-emerald-500/30 bg-emerald-500/[0.03]'
      : selected
      ? 'border-rose-500/50 bg-rose-500/[0.05]'
      : 'border-line bg-ink/40'
    : selected
    ? 'border-amber bg-amber/[0.06]'
    : 'border-line bg-ink/40 hover:border-amber/50';
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-lg border p-4 transition-colors ${borderClass}`}
    >
      <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
        {label}
      </p>
      <p className="mt-1 text-sm font-semibold text-text">{sub}</p>
      <p className="mt-3 text-xs leading-relaxed text-text/80">
        <span className="font-mono uppercase tracking-widest text-muted">
          Best for ·{' '}
        </span>
        {best}
      </p>
    </button>
  );
}

function PromptCard({
  id,
  label,
  tag,
  body,
  picked,
  onClick,
}: {
  id: 'weak' | 'strong';
  label: string;
  tag: 'Weak' | 'Strong';
  body: string;
  picked: 'weak' | 'strong' | null;
  onClick: () => void;
}) {
  const selected = picked === id;
  const showResult = picked !== null;
  const isStrong = id === 'strong';
  const tagColor = tag === 'Strong' ? 'text-emerald-300' : 'text-rose-300';
  const tagBg = tag === 'Strong' ? 'bg-emerald-500/20' : 'bg-rose-500/20';
  const borderClass = showResult
    ? isStrong && selected
      ? 'border-emerald-500/50 bg-emerald-500/[0.05]'
      : isStrong
      ? 'border-emerald-500/30 bg-emerald-500/[0.03]'
      : selected
      ? 'border-rose-500/50 bg-rose-500/[0.05]'
      : 'border-line bg-ink/40'
    : selected
    ? 'border-amber bg-amber/[0.06]'
    : 'border-line bg-ink/40 hover:border-amber/50';
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-lg border p-4 transition-colors ${borderClass}`}
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
          {label}
        </p>
        <span
          className={`rounded-md ${tagBg} px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tagColor}`}
        >
          {tag}
        </span>
      </div>
      <p className="mt-3 whitespace-pre-wrap text-xs leading-relaxed text-text/90">
        {body}
      </p>
    </button>
  );
}

function ZonePick({
  id,
  label,
  sub,
  zone,
  correct,
  onClick,
}: {
  id: 'trust' | 'verify' | 'walk-away';
  label: string;
  sub: string;
  zone: 'trust' | 'verify' | 'walk-away' | null;
  correct: 'trust' | 'verify' | 'walk-away';
  onClick: () => void;
}) {
  const selected = zone === id;
  const showResult = zone !== null;
  const isRight = id === correct;
  const borderClass = showResult
    ? isRight && selected
      ? 'border-emerald-500/50 bg-emerald-500/[0.05]'
      : isRight
      ? 'border-emerald-500/30 bg-emerald-500/[0.03]'
      : selected
      ? 'border-rose-500/50 bg-rose-500/[0.05]'
      : 'border-line bg-ink/40'
    : selected
    ? 'border-amber bg-amber/[0.06]'
    : 'border-line bg-ink/40 hover:border-amber/50';
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-lg border p-4 transition-colors ${borderClass}`}
    >
      <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{sub}</p>
    </button>
  );
}

function AuditPanel() {
  const claims = [
    {
      claim: '"Three nearby districts already have varsity esports teams."',
      check:
        'You did not put this in the prompt. AI inferred it. Open one tab. Search "[your district] esports varsity" and the neighboring districts. If true, keep it. If not, cut it.',
      method: 'Cross-check',
    },
    {
      claim: '"Ms. Reyes in the CS department has agreed to serve as advisor."',
      check:
        'You wrote this. Did you actually ask her? If not, do that today before the email goes out. Never send a claim under your name that you have not verified yourself.',
      method: 'Ask a human',
    },
    {
      claim: '"Last year ours sent two students to a regional tournament."',
      check:
        'AI invented this to make the email more compelling. Cross-check with last year\'s yearbook or the activities director. If false, cut the whole sentence. The pitch survives without it.',
      method: 'Cross-check',
    },
  ];
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Verify · Three claims to check before this email goes out
        </p>
      </div>
      <div className="divide-y divide-line">
        {claims.map((c, i) => (
          <div key={i} className="space-y-2 px-5 py-4">
            <div className="flex items-baseline gap-3">
              <span className="font-mono text-[11px] uppercase tracking-widest text-rose-300">
                Claim {String(i + 1).padStart(2, '0')}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-amber">
                Method · {c.method}
              </span>
            </div>
            <p className="text-sm italic text-text/90">{c.claim}</p>
            <p className="text-sm text-text/80">{c.check}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ModeButton({
  label,
  sub,
  selected,
  verdict,
  onClick,
}: {
  label: string;
  sub: string;
  selected: boolean;
  verdict: 'right' | 'wrong' | null;
  onClick: () => void;
}) {
  const borderClass =
    verdict === 'right' && selected
      ? 'border-emerald-500/50 bg-emerald-500/[0.05]'
      : verdict === 'right' && !selected
      ? 'border-emerald-500/30 bg-emerald-500/[0.03]'
      : verdict === 'wrong' && selected
      ? 'border-rose-500/50 bg-rose-500/[0.05]'
      : selected
      ? 'border-amber bg-amber/[0.06]'
      : 'border-line bg-ink/40 hover:border-amber/50';
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-lg border p-3 transition-colors ${borderClass}`}
    >
      <p
        className={`font-mono text-[10px] uppercase tracking-widest ${
          selected ? 'text-amber' : 'text-muted'
        }`}
      >
        {label}
      </p>
      <p className="mt-1.5 text-xs text-text/90">{sub}</p>
    </button>
  );
}

function CompareView() {
  return (
    <div className="my-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-5">
      <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
        The before / between / after
      </p>
      <p className="mt-2 text-sm leading-relaxed text-text/90">
        Three artifacts now exist: your 60-second pitch (Stage 1), the AI
        draft (Stage 3), and your shipped email (Stage 5). The gap between
        the first and the third is the learning. Look at it once. That gap
        is what got sharper this week.
      </p>
    </div>
  );
}

// ---------- Next up ----------

function NextUp() {
  return (
    <div className="mt-12 rounded-lg border border-line bg-surface p-6" data-nextup-card>
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted">
        Up next · Week 3
      </p>
      <div className="mt-2 flex items-start justify-between gap-6">
        <div>
          <h3 className="text-xl font-semibold text-text">
            Building with AI
          </h3>
          <p className="mt-2 text-sm text-muted">
            Week 1 was the ground. Week 2 was the loop. Week 3 is when you
            stop using AI on one task and start composing it into longer
            work. Multi-step projects. Custom workflows. The same posture,
            scaled up.
          </p>
        </div>
        <button className="shrink-0 rounded-md border border-line px-4 py-2 text-sm text-muted hover:border-amber hover:text-text">
          Coming soon
        </button>
      </div>
    </div>
  );
}

// ---------- Shared primitives ----------

function Section({
  id,
  label,
  eyebrow,
  accent = 'plain',
  children,
}: {
  id: string;
  label: string;
  eyebrow: string;
  accent?: 'amber' | 'blue' | 'rose' | 'plain';
  children: React.ReactNode;
}) {
  const colorMap = {
    amber: 'text-amber',
    blue: 'text-[#7DC8E8]',
    rose: 'text-[#E89F8B]',
    plain: 'text-text',
  };
  return (
    <section
      id={id}
      className="scroll-mt-24 border-t border-line py-12 first-of-type:border-t-0 first-of-type:pt-0"
    >
      <div className="mb-6">
        <p
          className={`font-mono text-[11px] uppercase tracking-[0.18em] ${colorMap[accent]}`}
        >
          {label}
        </p>
        <p className="mt-1.5 text-sm text-muted">{eyebrow}</p>
      </div>
      <div className="space-y-4 text-base leading-relaxed text-text/90">
        {children}
      </div>
    </section>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    { id: 'sec-brief', label: 'The brief' },
    { id: 'sec-loop', label: 'The loop' },
    { id: 'sec-stage-1', label: '1 · Think first' },
    { id: 'sec-stage-2', label: '2 · Pick the tool' },
    { id: 'sec-stage-3', label: '3 · Write the prompt' },
    { id: 'sec-stage-4', label: '4 · Audit' },
    { id: 'sec-stage-5', label: '5 · Posture and ship' },
    { id: 'sec-lock-card', label: 'Lock card' },
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this capstone
      </p>
      <ul className="mt-4 space-y-1">
        {items.map((it, i) => {
          const active = i === activeIndex;
          const done = i < activeIndex;
          return (
            <li key={it.id}>
              <a
                href={`#${it.id}`}
                className={`flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:bg-elevated/60 hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border font-mono text-[10px] ${
                    active
                      ? 'border-amber bg-amber text-ink'
                      : done
                      ? 'border-amber/40 text-amber'
                      : 'border-line text-muted'
                  }`}
                >
                  {done ? '✓' : i + 1}
                </span>
                {it.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
